import {put, call} from 'redux-saga/effects';
import InvoiceBatchItem from "../../entities/invoiceBatchItem";
import * as Actions from "../actions/invoiceBatchItemActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type InvoiceBatchItemAction = {
    invoiceBatchItemId: string;
    invoiceBatchItem: InvoiceBatchItem;
    invoiceBatchItems: InvoiceBatchItem[];
};

export function* fetchOneInvoiceBatchItem(action: InvoiceBatchItemAction) {
	try {
		const invoiceBatchItem: InvoiceBatchItem = yield call(api.findOne, 'invoiceBatchItem', action.invoiceBatchItemId);

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCHITEM_FETCH_ONE_SUCCESS, invoiceBatchItemId: action.invoiceBatchItemId, invoiceBatchItem});
	}
	catch(err) {
		yield put({type: Actions.INVOICEBATCHITEM_FETCH_ONE_FAILURE, invoiceBatchItemId: action.invoiceBatchItemId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {InvoiceBatchItemAction} action
 */
export function* saveInvoiceBatchItem(action: InvoiceBatchItemAction) {
    // console.debug('⮂ INVOICEBATCHITEM_SAVE saga', action.invoiceBatchItem?.id);
    
	if (action.invoiceBatchItem === undefined || action.invoiceBatchItem === null) {
		console.error('saveInvoiceBatchItem received a null');
		throw new Error('saveInvoiceBatchItem received a null');
	}

	if (!(action.invoiceBatchItem instanceof InvoiceBatchItem)) {
		action.invoiceBatchItem = new InvoiceBatchItem(action.invoiceBatchItem);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoiceBatchItem', action.invoiceBatchItem);
		if (addResult.isErr()) {
			console.error(`⮂ saveInvoiceBatchItem: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICEBATCHITEM_SAVE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCHITEM_SAVE, invoiceBatchItem: action.invoiceBatchItem});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'invoiceBatchItem', action.invoiceBatchItem.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.INVOICEBATCHITEM_SAVE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {InvoiceBatchItemAction} action
 * @param {InvoiceBatchItem[]} action.invoiceBatchItems
 */
export function* saveManyInvoiceBatchItems(action: InvoiceBatchItemAction) {
    // console.debug('⮂ INVOICEBATCHITEM_SAVE_MANY saga', action.invoiceBatchItems.map(invoiceBatchItem => invoiceBatchItem.id).join(','));
    
    if (!Array.isArray(action.invoiceBatchItems)) {
        console.error('saveManyInvoiceBatchItems expected an array, received', action.invoiceBatchItems);
        throw new Error('saveManyInvoiceBatchItems expected an array');
    }

	try {
		const toSave: InvoiceBatchItem[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.invoiceBatchItems.length; i++) {
			const invoiceBatchItem = action.invoiceBatchItems[i];
			invoiceBatchItem.persisted = false;
			toSave.push(invoiceBatchItem);
		}

		const addResult = yield call(idb.add, 'invoiceBatchItem', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyInvoiceBatchItems: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICEBATCHITEM_SAVE_MANY_FAILURE, invoiceBatchItems: action.invoiceBatchItems, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCHITEM_SAVE_MANY, invoiceBatchItems: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.INVOICEBATCHITEM_SAVE_MANY_FAILURE, invoiceBatchItems: action.invoiceBatchItems, status: 500, err});

		console.error(`Error saving ${action.invoiceBatchItems.length} invoiceBatchItems to database`, err);
	}
}

/**
 * @param {InvoiceBatchItemAction} action
 */
export function* removeInvoiceBatchItem(action: InvoiceBatchItemAction) {
    const userId = getCurrentUserId();
	if (!userId || action.invoiceBatchItem === undefined || action.invoiceBatchItem === null || !(action.invoiceBatchItem instanceof InvoiceBatchItem)) {
			console.error('removeInvoiceBatchItem expected type InvoiceBatchItem, received', action.invoiceBatchItem);
			throw new Error('removeInvoiceBatchItem expected type InvoiceBatchItem');
		}

	try {
		// Mark as deleted
		action.invoiceBatchItem.status = 'D';
		action.invoiceBatchItem.addedOn = newHRTimestamp();
		action.invoiceBatchItem.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoiceBatchItem', action.invoiceBatchItem);
		if (addResult.isErr()) {
			console.error(`⮂ removeInvoiceBatchItem: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICEBATCHITEM_REMOVE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCHITEM_REMOVE, invoiceBatchItem: action.invoiceBatchItem});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.INVOICEBATCHITEM_REMOVE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this InvoiceBatchItem.
	*
	* @param {InvoiceBatchItemAction} action
	*/
export function* restoreInvoiceBatchItem(action: InvoiceBatchItemAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreInvoiceBatchItem expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.invoiceBatchItem === undefined || action.invoiceBatchItem === null) {
        console.error('restoreInvoiceBatchItem expected a InvoiceBatchItem, received null');
        throw new Error('restoreInvoiceBatchItem expected a InvoiceBatchItem, received null');
    }
    else if (!(action.invoiceBatchItem instanceof InvoiceBatchItem)) action.invoiceBatchItem = new InvoiceBatchItem(action.invoiceBatchItem); 
    
	try {
		// Mark as restored
		action.invoiceBatchItem.status = 'U';
		action.invoiceBatchItem.addedOn = newHRTimestamp();
		action.invoiceBatchItem.addedBy = userId;
		action.invoiceBatchItem.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'invoiceBatchItem', action.invoiceBatchItem);
		if (addResult.isErr()) {
			console.error(`⮂ restoreInvoiceBatchItem: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.INVOICEBATCHITEM_RESTORE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.INVOICEBATCHITEM_RESTORE, invoiceBatchItem: action.invoiceBatchItem});
	}
	catch(err) {
		yield put({type: Actions.INVOICEBATCHITEM_RESTORE_FAILURE, invoiceBatchItem: action.invoiceBatchItem, status: 500, err});

		console.error('Error restoring from database', err);
	}
}